import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Star } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import api, { getProperties } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function AdminVendors() {
  const [vendors, setVendors] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("");
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    status: "active",
    supported_experiences: []
  });
  const [saving, setSaving] = useState(false);

  // When switching properties in the form, clear stale experience assignments
  const handlePropertyChange = (newPropId) => {
    setSelectedProperty(newPropId);
    setFormData(prev => ({ ...prev, supported_experiences: [] }));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vData, eData, pData] = await Promise.all([
        api.get("/vendors").then(res => res.data),
        api.get("/experiences?active_only=false").then(res => res.data),
        getProperties()  // returns array directly
      ]);
      setVendors(Array.isArray(vData) ? vData : []);
      setExperiences(Array.isArray(eData) ? eData : []);
      const filtered = Array.isArray(pData)
        ? pData.filter(p => p.property_id !== "global" && p.property_id !== "temp")
        : [];
      setProperties(filtered);
      // Auto-select first property if nothing is selected yet
      if (filtered.length > 0 && !selectedProperty) {
        setSelectedProperty(filtered[0].property_id);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (vendor = null) => {
    if (vendor) {
      setEditingVendor(vendor);
      setFormData({
        name: vendor.name,
        phone: vendor.phone,
        status: vendor.status,
        supported_experiences: vendor.supported_experiences || []
      });
      setSelectedProperty(vendor.property_id || selectedProperty);
    } else {
      setEditingVendor(null);
      setFormData({
        name: "",
        phone: "",
        status: "draft",
        supported_experiences: []
      });
    }
    setIsDialogOpen(true);
  };

  const handleAddExperience = (expId) => {
    if (!expId || formData.supported_experiences.some(e => e.experience_id === expId)) return;
    
    // Check limit of 5 max as per user requirement
    if (formData.supported_experiences.length >= 5) {
      toast.error("A vendor can support a maximum of 5 experiences.");
      return;
    }
    
    setFormData({
      ...formData,
      supported_experiences: [
        ...formData.supported_experiences,
        { experience_id: expId, commission_percentage: 10 }
      ]
    });
  };

  const handleRemoveExperience = (expId) => {
    setFormData({
      ...formData,
      supported_experiences: formData.supported_experiences.filter(e => e.experience_id !== expId)
    });
  };

  const handleCommissionChange = (expId, value) => {
    setFormData({
      ...formData,
      supported_experiences: formData.supported_experiences.map(e => 
        e.experience_id === expId ? { ...e, commission_percentage: parseFloat(value) || 0 } : e
      )
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData, property_id: selectedProperty };
      if (editingVendor) {
        await api.put(`/vendors/${editingVendor.id}`, payload);
        toast.success("Vendor updated successfully");
      } else {
        await api.post("/vendors", payload);
        toast.success("Vendor created successfully");
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Failed to save vendor");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVendor = async (vendor) => {
    if (!window.confirm(`Delete vendor "${vendor.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/vendors/${vendor.id}`);
      toast.success("Vendor deleted");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete vendor");
    }
  };

  const getExperienceTitle = (id) => {
    const exp = experiences.find(e => e.id === id);
    return exp ? exp.title : "Unknown Experience";
  };

  return (
    <AdminLayout title="Vendors">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Vendor Management</h2>
          <p className="text-muted-foreground text-sm mt-1">Manage local vendors, mappings, and commissions.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedProperty} onValueChange={handlePropertyChange}>
            <SelectTrigger className="w-48 bg-white">
              <SelectValue placeholder="Select Property" />
            </SelectTrigger>
            <SelectContent>
              {properties.map((p) => (
                <SelectItem key={p.property_id} value={p.property_id}>
                  {p.property_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="h-4 w-4 mr-2" /> Add Vendor
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : vendors.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-100">
          <p className="text-gray-500 mb-4">No vendors found.</p>
          <Button variant="outline" onClick={() => handleOpenDialog()}>Create your first vendor</Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {vendors.map((vendor) => (
            <div key={vendor.id} className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm relative">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-lg">{vendor.name}</h3>
                  <p className="text-sm text-gray-500">{vendor.phone}</p>
                </div>
                <div className={`px-2 py-1 text-xs rounded-full ${
                  vendor.status === 'active' ? 'bg-green-100 text-green-700' :
                  vendor.status === 'disabled' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {vendor.status.toUpperCase()}
                </div>
              </div>

              <div className="flex items-center text-yellow-500 mb-4 text-sm font-medium">
                <Star className="h-4 w-4 mr-1 fill-current" />
                Score: {vendor.score?.current_score || 0}
                <span className="text-gray-400 ml-2 font-normal text-xs">
                  ({vendor.score?.accepted_count || 0} claimed, {vendor.score?.rejected_count || 0} rejected)
                </span>
              </div>

              <div className="space-y-2 mb-6">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Assigned Experiences</p>
                {vendor.supported_experiences?.length > 0 ? (
                  vendor.supported_experiences.map((exp, i) => (
                    <div key={i} className="text-sm bg-gray-50 px-3 py-2 rounded flex justify-between border border-gray-100">
                      <span className="truncate pr-2">{getExperienceTitle(exp.experience_id)}</span>
                      <span className="font-medium shrink-0">{exp.commission_percentage}%</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-400 italic">No experiences assigned</p>
                )}
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" size="sm" onClick={() => handleOpenDialog(vendor)}>
                  <Pencil className="h-4 w-4 mr-2" /> Edit
                </Button>
                <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" size="sm" onClick={() => handleDeleteVendor(vendor)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle>
            <DialogDescription>
              Configure vendor details and experience mappings.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vendor Name</Label>
                <Input required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g. Ramesh Tours" />
              </div>
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input required value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="10-digit number" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={v => setFormData({...formData, status: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active (Receives Broadcasts)</SelectItem>
                  <SelectItem value="disabled">Disabled (Temporarily Offline)</SelectItem>
                  <SelectItem value="draft">Draft (Onboarding)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Property selector inside dialog — always visible */}
            <div className="space-y-2">
              <Label>Property <span className="text-red-500">*</span></Label>
              <Select value={selectedProperty} onValueChange={handlePropertyChange} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select property..." />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.property_id} value={p.property_id}>
                      {p.property_name || p.property_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3 pt-4 border-t">
              <div className="flex justify-between items-center">
                <Label>Assigned Experiences (Max 5)</Label>
              </div>
              
              <Select onValueChange={handleAddExperience} disabled={formData.supported_experiences.length >= 5}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Add an experience..." />
                </SelectTrigger>
                <SelectContent>
                  {experiences
                    .filter(exp => !selectedProperty || exp.property_id === selectedProperty)
                    .map(exp => (
                    <SelectItem key={exp.id} value={exp.id} disabled={formData.supported_experiences.some(e => e.experience_id === exp.id)}>
                      {exp.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {formData.supported_experiences.map((exp, index) => (
                <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded border">
                  <div className="flex-1 truncate text-sm">
                    {getExperienceTitle(exp.experience_id)}
                  </div>
                  <div className="w-24 shrink-0 flex items-center">
                    <Input 
                      type="number" 
                      min="0" max="100" 
                      value={exp.commission_percentage} 
                      onChange={e => handleCommissionChange(exp.experience_id, e.target.value)}
                      className="h-8 text-right pr-1"
                    />
                    <span className="text-sm text-gray-500 ml-1">%</span>
                  </div>
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleRemoveExperience(exp.experience_id)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="pt-4 flex justify-end gap-2 border-t mt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving || !selectedProperty}>{saving ? "Saving..." : "Save Vendor"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
